import { useSelector } from 'react-redux';

import { canWrite, getAccessLevel, hasPermission } from '../constants/rbac';
import type { AccessLevel, Permission, Role } from '../constants/rbac';
import type { RootState } from '../store';

interface Permissions {
  role: Role | null;
  tenantId: string | null;
  /**
   * Mirrors authInitialized: false until Firebase has resolved whether a user
   * is signed in (and, if so, delivered the token whose claims carry the
   * role). Callers gate on it to avoid a flash of the wrong state - the same
   * reason SearchEntryAuthGuard renders null before authInitialized.
   */
  isReady: boolean;
  hasPermission: (permission: Permission) => boolean;
  canWrite: (permission: Permission) => boolean;
  getAccessLevel: (permission: Permission) => AccessLevel;
}

/**
 * The frontend's single read point for RBAC decisions.
 *
 * Wraps the pure helpers from constants/rbac around the role the
 * FirebaseAuthSessionBridge validated out of the signed token. FAIL CLOSED by
 * construction: role is null unless the token carried one of the seven known
 * role strings, and a null role resolves to 'none' for every permission - a
 * signed-in user with no role claim sees no RBAC-gated surface.
 *
 * UI gating is a courtesy, not the enforcement. The gateway middleware makes
 * the same decision from the same matrix on every request; hiding a control
 * here only spares the user a 403.
 */
export const usePermissions = (): Permissions => {
  const { role, tenantId, authInitialized } = useSelector((state: RootState) => state.auth);

  return {
    role,
    tenantId,
    isReady: authInitialized,
    hasPermission: (permission: Permission) => hasPermission(role, permission),
    canWrite: (permission: Permission) => canWrite(role, permission),
    getAccessLevel: (permission: Permission) => getAccessLevel(role, permission),
  };
};
